import moment from "moment";
import { Card, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { Add as AddIcon, CheckBoxOutlineBlank, CheckBoxOutlined, CheckCircle, Unpublished } from "@mui/icons-material";
import { Autocomplete, Box, Button, DataTable, useCommonComponentContext, IPropListContainer } from "@maysoft/common-component-react";

import usePost from "./usePost";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import PublishDialog from "../../components/Post/dialogPublishPost";
import PostService from "../../services/message/Post.service";

import { Constants } from "../../constants/constants";
import { IRecordPost, IRequestGetPaged } from "./usePost";
import { Mode, PostIsHighlighted, PublishedStatus } from "../../constants/enums";

interface IModel {
    requestData?: IRequestGetPaged;
    requestDataTemp?: IRequestGetPaged;
}

const PostListContainer = (props: IPropListContainer<IRequestGetPaged>) => {
    const Strings = useStrings();

    const { language, serviceCode, tenantCode, listMultiLanguage, onError, onHideLoading, onShowLoading } = useCommonComponentContext();

    const {
        listPostTags,
        listPostTypes,
        listPostHighlighted,
        getPublishedStatusValue,

        handleDeletePost,
        handleUnpublishPost,
    } = usePost();

    const [publishPostId, setPublishPostId] = useState<string>("");
    const [openPublishDialog, setOpenPublishDialog] = useState<boolean>(false);

    const [model, setModel] = useState<IModel>({} as IModel);
    const [dataRows, setDataRows] = useState<IRecordPost[]>([]);

    useEffect(() => {
        const requestData: IRequestGetPaged = {
            pageSize: props.requestGetPaged?.pageSize || model.requestData?.pageSize,
            pageNumber: props.requestGetPaged?.pageNumber || model.requestData?.pageSize,
            totalCount: props.requestGetPaged?.totalCount || model.requestData?.totalCount,
            published: !Helpers.isNullOrEmpty(props.requestGetPaged?.published) ? props.requestGetPaged.published : null,
            isHighlighted: props.requestGetPaged?.isHighlighted || model.requestData?.isHighlighted,
            postTag: props.requestGetPaged?.postTag || model.requestData?.postTag,
            orderBy: props?.requestGetPaged?.orderBy
        };
        getPagedPost(requestData);
    }, [props.requestGetPaged]);

    const getPagedPost = async (requestData?: IRequestGetPaged) => {
        try {
            onShowLoading();

            const pageSize = requestData?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(requestData?.pageNumber || 1, pageSize, requestData?.totalCount || 0);

            const newReq: any = {
                pageSize: pageSize,
                pageNumber: pageNumber,
                published: requestData?.published,
                isHighlighted: requestData?.isHighlighted,
                postTag: requestData?.postTag,
                type: requestData?.type,
                serviceCode: serviceCode,
                tenantCode: tenantCode,
            };

            const result = await PostService.getPaged(newReq);

            setDataRows([...(result?.items || [])]);
            setModel({
                requestData: {
                    ...requestData,
                    totalCount: result.totalCount,
                },
                requestDataTemp: {
                    ...requestData,
                    totalCount: result.totalCount,
                },
            });

            let query: string = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
            if (!Helpers.isNullOrEmpty(newReq?.type)) {
                query += `&type=${newReq?.type}`;
            }
            if (!Helpers.isNullOrEmpty(newReq?.postTag)) {
                query += `&postTag=${newReq?.postTag}`;
            }
            if (!Helpers.isNullOrEmpty(newReq?.isHighlighted)) {
                query += `&isHighlighted=${newReq?.isHighlighted}`;
            }

            props.onGetPaged({ query, totalCount: result.totalCount || 0 });
        } catch (error) {
            const err = Helpers.renderExceptionError(error);
            onError(err);
            onHideLoading();
        } finally {
            onHideLoading();
        }
    };

    const handlePublish = (id: string) => {
        setPublishPostId(id);
        setOpenPublishDialog(true);
    };

    const filterForm = () => {
        return (
            <Grid className="mt-3" container spacing={2}>
                <Grid item xs={12}>
                    <Autocomplete
                        multiple
                        data={listPostTypes}
                        label={Strings.POST.TYPE}
                        placeholder={Strings.POST.TYPE}
                        defaultValue={model?.requestDataTemp?.type}
                        onChange={(value) => {
                            setModel({
                                ...model,
                                requestDataTemp: {
                                    ...model.requestDataTemp,
                                    type: value,
                                },
                            });
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Autocomplete
                        data={listPostTags}
                        label={Strings.POST.CATEGORY}
                        placeholder={Strings.POST.CATEGORY}
                        defaultValue={model?.requestDataTemp?.postTag}
                        onChange={(value: string) => {
                            setModel({
                                ...model,
                                requestDataTemp: {
                                    ...model.requestDataTemp,
                                    postTag: value,
                                },
                            });
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Autocomplete
                        data={listPostHighlighted}
                        label={Strings.POST.IS_HIGHTLIGHTED}
                        placeholder={Strings.POST.IS_HIGHTLIGHTED}
                        defaultValue={model?.requestDataTemp?.isHighlighted}
                        onChange={(value: number) => {
                            setModel({
                                ...model,
                                requestDataTemp: {
                                    ...model.requestDataTemp,
                                    isHighlighted: value,
                                },
                            });
                        }}
                    />
                </Grid>
            </Grid>
        );
    };

    return (
        <Box>
            <PublishDialog
                postId={publishPostId}
                open={openPublishDialog}
                onClose={() => setOpenPublishDialog(false)}
                onPublishSuccess={() => {
                    getPagedPost({ ...model.requestData });
                    setOpenPublishDialog(false);
                }}
            />

            <Box display="flex" justifyContent="end" mb={3}>
                <Button
                    onClick={() => {
                        props.onNavigate({ id: undefined, mode: Mode.Create });
                    }}
                >
                    <AddIcon />
                    &nbsp;
                    {Strings.Common.ADD_NEW}
                </Button>
            </Box>

            <Card>
                <Box p={2}>
                    <DataTable
                        rowPerPage={model.requestData?.pageSize}
                        pageNumber={model.requestData?.pageNumber}
                        totalCount={model.requestData?.totalCount}
                        onChangePageSize={(pageSize) => {
                            getPagedPost({ ...model?.requestData, pageSize });
                        }}
                        onChangePageNumber={(pageNumber) => {
                            getPagedPost({ ...model?.requestData, pageNumber });
                        }}
                        filterForm={filterForm()}
                        onFilter={() => {
                            getPagedPost({ ...model?.requestDataTemp, pageNumber: 1 });
                        }}
                        onCloseFilter={() => {
                            const temp = { ...model?.requestData };
                            setModel({ ...model, requestDataTemp: temp });
                        }}
                        onReset={() => {
                            getPagedPost({
                                pageNumber: 1,
                                pageSize: Constants.ROW_PER_PAGE,
                                published: !Helpers.isNullOrEmpty(model.requestData.published) ? model.requestData.published : null,
                            });
                        }}
                        table={{
                            rows: dataRows || [],
                            columns: [
                                {
                                    Header: Strings.POST.TITLE,
                                    accessor: "title",
                                    width: "200px",
                                    Cell: ({ value }: any) => {
                                        const newVal = Helpers.getDataMultiLanguage({
                                            data: value,
                                            currentLanguage: language,
                                            listLanguage: listMultiLanguage,
                                        });
                                        return <>{newVal?.value?.[language]}</>;
                                    },
                                },
                                {
                                    Header: Strings.POST.TYPE,
                                    accessor: "type",
                                    width: "90px",
                                    Cell: ({ value }: any) => <>{listPostTypes.find((el) => `${el.code}` === `${value}`)?.name || ""}</>,
                                },
                                {
                                    Header: Strings.POST.EVENT_TIME,
                                    accessor: "eventTime",
                                    width: "120px",
                                    Cell: ({ value }: any) => (
                                        <>
                                            {Helpers.isNullOrEmpty(value) || value === 0 || value === "0"
                                                ? ""
                                                : moment(Number(value) * 1000).format(Constants.DateFormat.DDMMYYYY)}
                                        </>
                                    ),
                                },
                                {
                                    Header: Strings.POST.HIGHLIGHTED,
                                    accessor: "isHighlighted",
                                    width: "100px",
                                    Cell: ({ value }: any) => (
                                        <>{Number(value || 0) === PostIsHighlighted.None ? <CheckBoxOutlineBlank /> : <CheckBoxOutlined />}</>
                                    ),
                                },
                                {
                                    Header: Strings.POST.PUBLISH_STATUS,
                                    accessor: "publishedStatus",
                                    width: "auto",
                                    Cell: (row) => {
                                        const publishReady = Boolean(row?.row?.original?.publishReady);
                                        return getPublishedStatusValue({ status: Number(row.value), publishReady });
                                    },
                                },
                            ],
                        }}
                        actionList={(row) => [
                            {
                                key: "detail",
                                iconName: "view",
                                title: Strings.Common.VIEW_DETAIL,
                                callback: (row) => props.onNavigate({ id: row?.id, mode: Mode.View }),
                            },

                            {
                                key: "edit",
                                iconName: "edit",
                                title: Strings.Common.EDIT,
                                callback: (row) => props.onNavigate({ id: row?.id, mode: Mode.Update }),
                            },

                            [PublishedStatus.Draft].includes(row?.publishedStatus) && {
                                key: "publish",
                                title: Strings.POST.PUBLISH,
                                icon: <CheckCircle fontSize="small" />,
                                callback: (row) => handlePublish(row.id),
                            },

                            [PublishedStatus.Published].includes(row?.publishedStatus) && {
                                key: "unpublish",
                                title: Strings.POST.STOP_PUBLISH,
                                icon: <Unpublished fontSize="small" />,
                                callback: (row) =>
                                    handleUnpublishPost({
                                        id: row.id,
                                        onCallBack: () => {
                                            getPagedPost({ ...model.requestData });
                                        },
                                    }),
                            },

                            {
                                key: "delete",
                                iconName: "delete",
                                title: Strings.Common.DELETE,
                                callback: (row) =>
                                    handleDeletePost({
                                        id: row.id,
                                        onCallBack: () => {
                                            getPagedPost({ ...model.requestData });
                                        },
                                    }),
                            },
                        ]}
                    />
                </Box>
            </Card>
        </Box>
    );
};

export default PostListContainer;
